// @flow
import React, { Fragment, type Node } from "react";
import { noop } from "lodash";
import type { NullaryFn, UnaryFn } from "@tvg/types/Functional";
import buildColor from "@fdr/static-ui/ColorPalette";
import Icon from "@fdr/static-ui/Icons";
import FDTVVideoOverlay from "@urp/livepage/src/components/FDTVVideoOverlay";
import {
  fullscreen,
  fullscreenExit,
  soundMute,
  soundPlay,
  info,
  play,
  videoReplay,
  pause,
  cross,
  forward,
  backward
} from "@fdr/static-ui/Icons/icons";
import ProcessingOverlay from "@fdr/static-ui/ProcessingOverlay";
import {
  Container,
  PlayButton,
  FeedBackContainer,
  RightBottomCornerOverlay,
  TopRigthCornerOverlay,
  CenterBottomOverlay,
  CenterSection,
  InnerPlayButtonWrapper,
  BaseButton
} from "./styled-components";
import Button from "../../../_atoms/Button";
import Text from "../../../_atoms/Text";

type VideoSkippingActionType = "fwd" | "rew";

type Props = {
  /**
   * Show video controlers
   */
  showControls: boolean,
  /**
   * Handle click over video container
   */
  clickHandler: UnaryFn<boolean, mixed>,
  /**
   * Is video playing
   */
  playing: boolean,
  /**
   * Click on play video
   */
  playHandler: NullaryFn<mixed>,
  /**
   * Click on skip video (fwd or rew)
   */
  skipToggleHandler: UnaryFn<VideoSkippingActionType, mixed>,
  /**
   * Flag to signal if forward skipping button is visible
   */
  fwdButtonIsVisible: boolean,
  /**
   * Flag to signal if rewind skipping button is visible
   */
  rewButtonIsVisible: boolean,
  /**
   * Is video muted
   */
  muted: boolean,
  /**
   * Is video has full unmute button
   */
  isFullUnmute: boolean,
  /**
   * Click on mute video button
   */
  muteHandler: NullaryFn<mixed>,
  /**
   * Is video fullscreen
   */
  fullscreen: boolean,
  /**
   * Handle fullscreen click
   */
  fullscreenHandler: NullaryFn<mixed>,
  /**
   * Is video ended
   */
  ended: boolean,
  /**
   * Initial load of video
   */
  loading: boolean,
  /**
   * Is video buffering
   */
  buffering: boolean,
  /**
   * Overlay over the video
   */
  renderOnOverlay: UnaryFn<boolean, Node>,
  /**
   * Is an error present
   */
  error: boolean,
  /**
   *
   */
  liveButtonCallback: UnaryFn<SyntheticEvent<HTMLButtonElement>, void>,
  /**
   * Is replay video
   */
  isReplay?: boolean,
  /**
   * Has live stream
   */
  hasLiveStream?: boolean,
  /**
   * Has replay stream
   */
  hasReplayStream?: boolean,

  /**
   * new stream video
   */
  hasNewStreamVideo: boolean,
  /**
   * close video callBack
   */
  closeHandler?: NullaryFn<void>,
  /**
   * is first loading
   */
  isFirstLoading: boolean,
  /**
   * is URP ctx
   */
  isURP?: boolean,
  /**
   * is FDTV ctx
   */
  isFDTV?: boolean,
  /**
   * userBalance for FDTV
   */
  userBalance?: number,
  /**
   * shouldDisplayUserBalance for FDTV
   */
  shouldDisplayUserBalance?: boolean
};

class OverlayControls extends React.Component<Props> {
  static defaultProps = {
    renderOnOverlay: noop,
    skipToggleHandler: noop,
    liveButtonCallback: noop,
    closeHandler: noop,
    showControls: true,
    loading: false,
    isReplay: true,
    hasLiveStream: false,
    hasReplayStream: false,
    isFullUnmute: false,
    fwdButtonIsVisible: false,
    rewButtonIsVisible: false,
    isFirstLoading: true,
    hasNewStreamVideo: false,
    isURP: false,
    isFDTV: false,
    userBalance: 0,
    shouldDisplayUserBalance: false
  };

  playIcon = () => {
    let returnIcon = play;

    if (!this.props.playing && this.props.isReplay && this.props.ended) {
      returnIcon = videoReplay;
    } else if (this.props.playing) {
      returnIcon = pause;
    }

    return returnIcon;
  };

  renderCenterSection = () => {
    const isEndOfReplay = this.props.ended && this.props.isReplay;
    const hasntEnded = !this.props.ended;
    const hasNoErrorsLoading = !this.props.loading && !this.props.error;
    const showPlayButton =
      (isEndOfReplay || hasntEnded) &&
      (hasNoErrorsLoading ||
        (!this.props.isReplay && this.props.hasNewStreamVideo));
    const iconPlay = this.playIcon();
    return (
      showPlayButton && (
        <CenterSection>
          {this.props.isReplay && this.props.rewButtonIsVisible && (
            <BaseButton
              className="rewButton"
              qaLabel="videoRewButton"
              onClick={this.props.skipToggleHandler("rew")}
            >
              <div>{this.renderSkipButtonLabel("rew")}</div>
            </BaseButton>
          )}
          <PlayButton
            onClick={this.props.playHandler}
            isPlaying={this.props.playing}
            isURP={this.props.isURP}
          >
            <InnerPlayButtonWrapper isPlayIcon={iconPlay === play}>
              <Icon
                size={20}
                icon={iconPlay}
                color={buildColor("white", "100")}
                qaLabel="play-icon"
              />
            </InnerPlayButtonWrapper>
          </PlayButton>
          {this.props.isReplay && this.props.fwdButtonIsVisible && (
            <BaseButton
              className="fwdButton"
              qaLabel="videoFwdButton"
              onClick={this.props.skipToggleHandler("fwd")}
            >
              <div>{this.renderSkipButtonLabel("fwd")}</div>
            </BaseButton>
          )}
        </CenterSection>
      )
    );
  };

  renderWatchButton = () => {
    const buttonLabel = this.props.isReplay
      ? "Switch to Live Feed"
      : "Switch to Replay";
    return (
      <Button
        type="primary"
        size="video"
        className={this.props.isReplay ? "replay" : ""}
        onClick={this.props.liveButtonCallback}
        text={buttonLabel}
      />
    );
  };

  renderSkipButtonLabel = (actionType: VideoSkippingActionType): Node =>
    actionType === "fwd" ? (
      <Icon
        size={20}
        icon={forward}
        color={buildColor("white", "100")}
        qaLabel="forward-icon"
      />
    ) : (
      <Icon
        size={20}
        icon={backward}
        color={buildColor("white", "100")}
        qaLabel="backward-icon"
      />
    );

  renderBottomRightCorner = () => (
    <RightBottomCornerOverlay>
      {this.props.isFullUnmute && !this.props.loading && this.props.playing ? (
        <BaseButton qaLabel="full-unMute-btn" onClick={this.props.muteHandler}>
          <div>
            <Icon size={20} icon={soundPlay} qaLabel="sound-play-icon" />
            <span>tap to unmute</span>
          </div>
        </BaseButton>
      ) : (
        <BaseButton onClick={this.props.muteHandler}>
          <div>
            <Icon
              size={20}
              icon={this.props.muted ? soundPlay : soundMute}
              color={buildColor("white", "100")}
            />
          </div>
        </BaseButton>
      )}
      <BaseButton onClick={this.props.fullscreenHandler}>
        <div>
          <Icon
            size={20}
            icon={this.props.fullscreen ? fullscreenExit : fullscreen}
            color={buildColor("white", "100")}
          />
        </div>
      </BaseButton>
    </RightBottomCornerOverlay>
  );

  renderTopRightCorner = () =>
    this.props.closeHandler !== noop && (
      <TopRigthCornerOverlay>
        <BaseButton qaLabel="close-btn" onClick={this.props.closeHandler}>
          <div>
            <Icon size={24} icon={cross} color={buildColor("white", "100")} />
          </div>
        </BaseButton>
      </TopRigthCornerOverlay>
    );

  renderBottomCenterSection = () => (
    <CenterBottomOverlay>
      {!this.props.isFDTV &&
        this.props.hasLiveStream &&
        this.props.hasReplayStream &&
        this.renderWatchButton()}
    </CenterBottomOverlay>
  );

  renderFDTVOverlay = () => (
    <FDTVVideoOverlay
      isReplay={this.props.isReplay}
      userBalance={this.props.userBalance}
      shouldDisplayUserBalance={this.props.shouldDisplayUserBalance}
    />
  );

  renderVideoOptions = () => {
    const isNewLiveVideo = this.props.hasNewStreamVideo && !this.props.isReplay;
    const showSection = !this.props.error || isNewLiveVideo;
    return (
      <Fragment>
        {/* this need to be replaced by feedback component */}
        {((!this.props.isReplay && this.props.ended) ||
          (this.props.error &&
            (this.props.isReplay || !this.props.hasNewStreamVideo))) && (
          <FeedBackContainer>
            <Icon icon={info} color={buildColor("white", "100")} size={20} />
            <Text fontSize={14} color={buildColor("white", "100")}>
              The streaming is not available
            </Text>
          </FeedBackContainer>
        )}
        {showSection && this.renderCenterSection()}
        {showSection && this.renderBottomRightCorner()}
        {showSection && this.renderBottomCenterSection()}
        {(showSection || this.props.isReplay) &&
          this.props.isFDTV &&
          !this.props.loading &&
          this.renderFDTVOverlay()}
      </Fragment>
    );
  };

  render() {
    const showOverLay =
      (this.props.buffering && !this.props.error) ||
      (this.props.loading && !this.props.error);

    const showDelayMessage =
      this.props.isFirstLoading &&
      !this.props.isReplay &&
      this.props.hasLiveStream;

    return (
      <Container
        onClick={() => this.props.clickHandler(true)}
        isVisible={this.props.showControls}
      >
        {this.props.showControls && this.renderVideoOptions()}
        {!this.props.isFDTV && this.renderTopRightCorner()}

        {showOverLay &&
          (this.props.isReplay || !this.props.hasNewStreamVideo) && (
            <ProcessingOverlay
              isBlackTransparent
              text={showDelayMessage ? "30 sec delay on real time" : ""}
              isURP={this.props.isURP}
            />
          )}
      </Container>
    );
  }
}

export default OverlayControls;
